import clsx from 'clsx';
import { flatten, isEmpty } from 'lodash';
import { useContext, useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import {
  Accordion,
  AccordionContent,
  AccordionProvider,
  AccordionTitle,
  AddCartSuccessModal,
  Card,
  FormInputCount,
  FormSelect,
  Icon,
  LeisureContext,
  Loader,
  Skeleton
} from 'components';
import { PAGE_TYPES } from 'constants';
import { capitalizeFirstLetter, formatLocalizedCurrency } from 'helpers';
import { useScreenResize } from 'hooks';

const TicketMasterSkeleton = () => {
  return (
    <div className="p-4 flex flex-col md:h-[35rem]">
      <div className="grow flex flex-col justify-between">
        <Skeleton classNames="h-6 w-32" />
        <Skeleton classNames="h-12 mb-4" />

        {Array.from({ length: 2 }).map((_, index) => (
          <div key={`tm-skeleton-${index}`} className="mb-4">
            <Skeleton classNames="h-8" />
            <div className="flex justify-between gap-6">
              <Skeleton classNames="h-8 w-1/3" />
              <Skeleton classNames="h-8 w-1/2" />
            </div>
          </div>
        ))}
      </div>

      <div>
        <div className="flex-center-between">
          <Skeleton classNames="h-6 w-32" />
          <Skeleton classNames="h-6 w-12" />
        </div>
        <Skeleton classNames="h-12 w-3/4 mt-4 mx-auto rounded-full" />
      </div>
    </div>
  );
};

const TicketMasterSummary = () => {
  const { t } = useTranslation();
  const { showElement, setShowElement } = useScreenResize();
  const { setValue, control, submitTicketmasterForm } = useFormContext();

  const {
    sessionsLoading,
    errorSessions,
    leisureDateOptions,
    navigateToSearch,
    emptySessions,
    placementOptions,
    ticketmasterLoading,
    initSessions,
    confirmationModal,
    setConfirmationModal
  } = useContext(LeisureContext);

  const sessionGroup = useWatch({ name: 'sessionGroup', control });

  const showErrorMessage = useMemo(() => !sessionsLoading && errorSessions, [sessionsLoading, errorSessions]);

  const pricesResume = useMemo(() => {
    if (isEmpty(sessionGroup)) return { total: 0 };
    const sessionPrices = flatten(sessionGroup?.map((session) => session.list));

    const total = sessionPrices.reduce((acc, { selectedQty, unitPrice }) => {
      return acc + selectedQty * unitPrice;
    }, 0);

    return { total: formatLocalizedCurrency(total) };
  }, [sessionGroup]);

  const onChangeLeisureDate = async (option) => {
    const newSession = { ...option, label: option.label?.props?.content || option.label };
    await initSessions(newSession);

    // Set date for the select
    setValue('leisureDate', option, { shouldValidate: true });
  };

  return (
    <>
      <div
        className={clsx('leisure-overlay', showElement.mobile && 'open-mobile')}
        onClick={() => setShowElement((prev) => ({ ...prev, mobile: false }))}
      />

      <Card onMobile={false} onTablet={false} className={clsx('leisure-summary', showElement.mobile && 'open-mobile')}>
        <>
          {sessionsLoading && <TicketMasterSkeleton />}

          {showErrorMessage && (
            <div className="p-4">
              <p className="mb-4">{t('products:emptySessions.content')}</p>
              <p>{t('products:emptySessions.complement')}</p>
            </div>
          )}

          {!sessionsLoading && !showErrorMessage && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submitTicketmasterForm();
                setShowElement((prev) => ({ ...prev, mobile: false }));
              }}
            >
              <div className="overflow-y-scroll px-6 pt-8 max-h-[20rem] lg:max-h-[35rem] lg:overflow-y-auto">
                {leisureDateOptions?.length > 1 ? (
                  <Controller
                    render={() => (
                      <FormSelect
                        options={leisureDateOptions || []}
                        onChange={onChangeLeisureDate}
                        name="leisureDate"
                        label={t('form:date')}
                        placeholder={t('form:placeholders.selectDate')}
                        icon={{ name: 'calendar-days', style: 'fad' }}
                        isSearchable={false}
                      />
                    )}
                    name="leisureDate"
                    control={control}
                    rules={{ required: true }}
                  />
                ) : (
                  <div className="flex items-center gap-2 pb-4">
                    <Icon name="calendar-days" iconStyle="fad" classes="text-yellow-500" />
                    {leisureDateOptions?.[0]?.label}
                  </div>
                )}

                {!emptySessions && (
                  <>
                    <p className="uppercase font-bold my-4">{t('products:tickets')}</p>

                    <AccordionProvider allowMultipleOpen>
                      {sessionGroup.map((session, i) => (
                        <Accordion key={`session-label-${i}`} itemIndex={i} className="mb-4">
                          <AccordionTitle icon={{ name: 'plus' }}>{session.categoryLabel}</AccordionTitle>

                          <AccordionContent>
                            {isEmpty(session.selectedPlacement) && <p>{t('products:emptyPlacement')}</p>}

                            {!isEmpty(session.selectedPlacement) && (
                              <>
                                <Controller
                                  render={({ field: { onChange } }) => (
                                    <FormSelect
                                      options={placementOptions(session.list[0]) || []}
                                      onChange={onChange}
                                      name={`sessionGroup[${i}].selectedPlacement`}
                                      label={t('form:placement')}
                                      className="mb-4"
                                    />
                                  )}
                                  name={`sessionGroup[${i}].selectedPlacement`}
                                  control={control}
                                />

                                {session.list.map((item, j) => {
                                  return (
                                    <div key={`session-pricing-${j}`} className="session-pricing">
                                      <p className="flex flex-col gap-y-1">
                                        <span className="text-sm">
                                          {capitalizeFirstLetter(item.customerNatureLabel)}
                                        </span>
                                        <span>{formatLocalizedCurrency(item.unitPrice)}</span>
                                      </p>

                                      <FormInputCount
                                        name={`sessionGroup[${i}].list[${j}].selectedQty`}
                                        disabled={session.selectedPlacement.qtyMax === 0}
                                        rules={{ min: 0, max: session.selectedPlacement.qtyMax }}
                                        label={t('form:quantity')}
                                        max={session.selectedPlacement.qtyMax}
                                      />
                                    </div>
                                  );
                                })}
                              </>
                            )}
                          </AccordionContent>
                        </Accordion>
                      ))}
                    </AccordionProvider>
                  </>
                )}
              </div>

              {!emptySessions && (
                <>
                  <div className="border-t border-gray-200 pt-4 w-[calc(100%-3rem)] mx-auto">
                    <div className="flex justify-between gap-x-2">
                      <p>{t('products:netPrice')}</p>
                      <span className="font-bold">{pricesResume.total}</span>
                    </div>
                  </div>

                  <button type="submit" className="btn mx-auto lg:my-4" disabled={ticketmasterLoading}>
                    {ticketmasterLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
                    {t('products:addToCart')}
                  </button>
                </>
              )}
            </form>
          )}

          {!sessionsLoading && !showErrorMessage && emptySessions && (
            <div className="p-4">
              <Trans i18nKey="products:emptyPrices">
                <button type="button" className="underline hover:no-underline" onClick={navigateToSearch}>
                  {t('clickHere')}
                </button>
              </Trans>
            </div>
          )}
        </>
      </Card>

      <div className={clsx('bg-white w-full lg:hidden print:hidden', showElement.mobile && 'hidden')}>
        <button
          type="button"
          className="btn mx-auto"
          onClick={() => setShowElement((prev) => ({ ...prev, mobile: true }))}
          disabled={ticketmasterLoading}
        >
          {ticketmasterLoading && <Loader loaderClass="w-4 h-4 text-white mr-2" />}
          {t('products:addToCart')}
        </button>
      </div>

      <AddCartSuccessModal
        pageType={PAGE_TYPES.leisures}
        confirmationModal={confirmationModal}
        setConfirmationModal={setConfirmationModal}
      />
    </>
  );
};

export default TicketMasterSummary;
